import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { currencyService } from '../services/currencyService';

const PaymentMethods = () => {
  const [, setCountry] = useState(currencyService.getCountry());

  useEffect(() => {
    const unsubscribe = currencyService.subscribe((nextCountry) => {
      setCountry(nextCountry);
    });

    return unsubscribe;
  }, []);

  const showKlarna = currencyService.showKlarna();
  const formatPrice = (price: number) => currencyService.formatPrice(price);
  const total = 1800;
  const installment = currencyService.getKlarnaInstallmentAmount(total);

  return (
    <>
      <Helmet>
        <title>Moyens de Paiement - Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Decouvrez nos solutions de paiement: carte bancaire, Klarna en 3x sur projets eligibles jusqu a 2000 EUR, et solutions bancaires pour les projets plus importants."
        />
      </Helmet>

      <main className="min-h-screen py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h1 className="text-3xl font-bold mb-8">Moyens de Paiement</h1>

            <section className="mb-12">
              <h2 className="text-xl font-semibold mb-4">Paiement par Carte Bancaire</h2>
              <div className="bg-gray-50 rounded-xl p-6 mb-4">
                <div className="flex items-center gap-4 mb-4">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-8" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-8" />
                </div>
                <p className="text-gray-600">
                  Nous acceptons les paiements par carte bancaire Visa et Mastercard. Vos transactions sont sécurisées par le protocole SSL et nos partenaires de paiement certifiés.
                </p>
              </div>
            </section>

            {showKlarna && (
              <section className="mb-12">
              <h2 className="text-xl font-semibold mb-4">Paiement en 3x sans frais avec Klarna</h2>
              <div className="bg-gray-50 rounded-xl p-6 mb-4">
                <div className="flex items-center gap-4 mb-4">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/4/40/Klarna_Payment_Badge.svg" alt="Klarna" className="h-8" />
                </div>
                <p className="text-gray-600 mb-4">
                  Klarna reste une option de flexibilite pour certains projets eligibles. Le paiement est propose en 3 versements egaux, sans frais supplementaires, uniquement jusqu a {currencyService.getKlarnaMaxAmountEUR()} EUR.
                </p>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-500">Exemple pour un achat de {formatPrice(total)} :</p>
                  <ul className="mt-2 space-y-1 text-sm text-gray-600">
                    <li>• 1er versement : {formatPrice(installment)} à la commande</li>
                    <li>• 2 mensualités de {formatPrice(installment)}</li>
                    <li>• Coût total : {formatPrice(total)} (TAEG 0%)</li>
                  </ul>
                </div>
                <p className="mt-4 text-sm text-amber-700">
                  Au-dela de {currencyService.getKlarnaMaxAmountEUR()} EUR, le paiement se fait en une fois ou via devis / financement adapte.
                </p>
              </div>
              </section>
            )}

            <section className="mb-12">
              <h2 className="text-xl font-semibold mb-4">Financement Bancaire</h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-600 mb-4">
                  Pour les projets plus importants, nous collaborons avec des partenaires bancaires qui peuvent vous proposer des solutions de financement adaptées à votre situation.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Étude personnalisée de votre dossier
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Taux négociés avec nos partenaires
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Accompagnement dans vos démarches
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  * Sous réserve d'acceptation de votre dossier par nos partenaires bancaires.
                </p>
              </div>
            </section>

            <div className="text-center">
              <Link
                to="/contact"
                className="inline-block bg-gradient-to-r from-neon-orange to-[#FF4500] text-white px-8 py-4 rounded-full font-semibold text-lg relative overflow-hidden group"
              >
                <span className="relative z-10">Discuter de votre projet →</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 1 }}
                />
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default PaymentMethods;
